import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { WhiteInkOptionsPicker } from '../../../components/WhiteInkOptionsPicker';

/**
 * Step 4 — Shape (die-cut / standard) + dimensions (W × H + unit).
 * Shows a warning banner with a "Request Custom Quote" CTA when the
 * selected size exceeds the format's maximum.
 *
 * For transparent / clear / specialty products (Clear BOPP, Holographic,
 * Foil, DTF, etc.) the parent passes `whiteInkEligible=true` and we
 * render the WhiteInkOptionsPicker between Shape and Dimensions.
 */
export const Step4Size = ({
  selectedShape,
  setSelectedShape,
  width,
  setWidth,
  height,
  setHeight,
  unit,
  setUnit,
  checkSizeLimit,
  handleCustomQuoteRequest,
  // White-ink options — only rendered when whiteInkEligible is true.
  whiteInkEligible = false,
  whiteInkOption,
  setWhiteInkOption,
  // Compact mode — used by the Page-2 redesign where shape + dims share
  // a card with quantity. Collapses labels, tightens spacing, hides the
  // helper paragraph, and flattens the shape radios into a 2-col row.
  compact = false,
}) => {
  const sizeCheck = checkSizeLimit();
  return (
    <div className={compact ? 'space-y-3' : 'space-y-6'}>
      {/* Shape (Die-Cut vs Standard) — hidden in compact mode because
          the Page-2 layout already collects this via the more
          specific CUT SHAPE picker (Rectangle / Rounded / Circle /
          Custom) inside the artwork upload section. The parent
          QuoteCalculator auto-derives `selectedShape` from
          `page2CutlineShape` so order submission + pricing + the PDF
          export still receive the correct value. */}
      {!compact && (
        <div>
          <Label className="text-lg font-semibold mb-4 block">
            Shape
          </Label>
          <RadioGroup value={selectedShape} onValueChange={setSelectedShape}>
            <div className="flex gap-4">
              <div
                className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer flex-1 transition-colors ${
                  selectedShape === 'die-cut' ? 'border-[#6FBF73] bg-green-50 animate-pulse-ring' : 'border-gray-200 hover:border-[#6FBF73]/50'
                }`}
                onClick={() => setSelectedShape('die-cut')}
              >
                <RadioGroupItem value="die-cut" id="die-cut" />
                <Label htmlFor="die-cut" className="cursor-pointer font-semibold">Die-Cut</Label>
              </div>
              <div
                className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer flex-1 transition-colors ${
                  selectedShape === 'standard' ? 'border-[#6FBF73] bg-green-50 animate-pulse-ring' : 'border-gray-200 hover:border-[#6FBF73]/50'
                }`}
                onClick={() => setSelectedShape('standard')}
              >
                <RadioGroupItem value="standard" id="standard" />
                <Label htmlFor="standard" className="cursor-pointer font-semibold">Standard Shapes</Label>
              </div>
            </div>
          </RadioGroup>
          <p className="text-sm text-gray-600 mt-2">
            * Standard Shape: Squares, Circles, Rectangles in even half-inch increments.
          </p>
        </div>
      )}

      {/* iter114q — White Ink Options moved into Page2ArtworkSection
          (directly under the Cut Shape selector, themed to match the
          dark green card). The duplicate render here was retired so
          the customer doesn't see the same picker twice on Page 2. */}

      <div>
        <Label className={compact ? 'text-sm font-bold mb-2 block uppercase tracking-wide text-gray-700' : 'text-lg font-semibold mb-4 block'}>
          Dimensions
        </Label>
        <div className={compact ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-3 gap-4'}>
          <div>
            {!compact && <Label htmlFor="width">Width</Label>}
            <Input
              id="width"
              type="number"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              className={compact ? '' : 'mt-1'}
              step="0.5"
              min="0.5"
              placeholder={compact ? 'Width' : undefined}
              data-testid="quote-width-input"
            />
          </div>
          <div>
            {!compact && <Label htmlFor="height">Height</Label>}
            <Input
              id="height"
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              className={compact ? '' : 'mt-1'}
              step="0.5"
              min="0.5"
              placeholder={compact ? 'Height' : undefined}
              data-testid="quote-height-input"
            />
          </div>
          <div>
            {!compact && <Label htmlFor="unit">Unit</Label>}
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className={compact ? '' : 'mt-1'}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inches">Inches</SelectItem>
                <SelectItem value="centimeters">Centimeters</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {sizeCheck.message && (
          <div className={`bg-yellow-50 border-l-4 border-yellow-400 ${compact ? 'p-2 mt-2' : 'p-4 mt-4'}`}>
            <div className="flex items-start">
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>⚠️ Size Exceeds Maximum:</strong>
                </p>
                <p className="text-sm text-yellow-700 mt-1">{sizeCheck.message}</p>
                <Button
                  onClick={handleCustomQuoteRequest}
                  className="mt-3 bg-yellow-600 hover:bg-yellow-700 text-white text-sm"
                >
                  Request Custom Quote
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
